import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { MARGIN_PRESETS, PAGE_SIZES, THEMES } from "../../lib/pages";

const Choice = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-primary bg-primary/5 text-primary" : "hover:bg-accent"}`}>{children}</button>
);

const PaperThumb = ({ s }) => {
  const p = PAGE_SIZES[s.size];
  const land = s.orientation === "landscape";
  const w = land ? p.h : p.w, h = land ? p.w : p.h;
  const k = 120 / Math.max(w, h);
  const m = s.margins;
  return (
    <div className="flex items-center justify-center h-[140px]">
      <div className="relative bg-white border shadow-sm" style={{ width: w * k, height: h * k }}>
        <div className="absolute border border-dashed border-primary/60" style={{ top: m.top * k, left: m.left * k, right: m.right * k, bottom: m.bottom * k }} />
      </div>
    </div>
  );
};

export const PageSetupDialog = ({ tab, onTab, onClose, settings, setSettings }) => {
  const set = (patch) => setSettings({ ...settings, ...patch });
  const setM = (k, v) => set({ margins: { ...settings.margins, [k]: Math.max(0, Math.min(100, Number(v) || 0)) } });
  const wm = settings.watermark;
  const pn = settings.pageNumbers;
  return (
    <Dialog open={!!tab} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="page-setup-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Page setup</DialogTitle><DialogDescription>Changes apply instantly to the page, print preview and exports.</DialogDescription></DialogHeader>
        <Tabs value={tab || "paper"} onValueChange={onTab}>
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="paper" data-testid="setup-tab-paper">Paper</TabsTrigger>
            <TabsTrigger value="margins" data-testid="setup-tab-margins">Margins</TabsTrigger>
            <TabsTrigger value="theme" data-testid="setup-tab-theme">Theme</TabsTrigger>
            <TabsTrigger value="watermark" data-testid="setup-tab-watermark">Watermark</TabsTrigger>
            <TabsTrigger value="numbers" data-testid="setup-tab-numbers">Numbers</TabsTrigger>
          </TabsList>
          <div className="grid grid-cols-[1fr_150px] gap-6 pt-4 min-h-[230px]">
            <div>
              <TabsContent value="paper" className="mt-0 space-y-4">
                <div className="space-y-2"><Label>Paper size</Label>
                  <div className="grid grid-cols-2 gap-2">{Object.entries(PAGE_SIZES).map(([k, p]) => <Choice key={k} on={settings.size === k} onClick={() => set({ size: k })} testid={`setup-size-${k}`}>{p.label}</Choice>)}</div>
                </div>
                <div className="space-y-2"><Label>Orientation</Label>
                  <div className="grid grid-cols-2 gap-2">{["portrait", "landscape"].map((o) => <Choice key={o} on={settings.orientation === o} onClick={() => set({ orientation: o })} testid={`setup-orientation-${o}`}><span className="capitalize">{o}</span></Choice>)}</div>
                </div>
              </TabsContent>
              <TabsContent value="margins" className="mt-0 space-y-4">
                <div className="grid grid-cols-2 gap-2">{Object.entries(MARGIN_PRESETS).map(([k, p]) => <Choice key={k} on={JSON.stringify(p.m) === JSON.stringify(settings.margins)} onClick={() => set({ margins: { ...p.m } })} testid={`setup-margins-${k}`}>{p.label}</Choice>)}</div>
                <div className="grid grid-cols-4 gap-3">
                  {["top", "bottom", "left", "right"].map((k) => (
                    <div key={k} className="space-y-1"><Label className="capitalize text-xs">{k} (mm)</Label>
                      <Input type="number" step="0.5" min="0" max="100" value={settings.margins[k]} onChange={(e) => setM(k, e.target.value)} data-testid={`setup-margin-${k}`} /></div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="theme" className="mt-0">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(THEMES).map(([k, t]) => (
                    <Choice key={k} on={settings.theme === k} onClick={() => set({ theme: k })} testid={`setup-theme-${k}`}>
                      <div className="flex items-center gap-2.5">
                        <span className="h-9 w-7 rounded-sm border shrink-0" style={{ background: t.bg, boxShadow: `inset 0 6px 0 ${t.heading}` }} />
                        <span><span className="block font-medium" style={{ fontFamily: t.font }}>{t.label}</span><span className="text-[11px] text-muted-foreground">{t.font.split(",")[0].replace(/'/g, "")}</span></span>
                      </div>
                    </Choice>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="watermark" className="mt-0 space-y-4">
                <div className="space-y-1"><Label>Watermark text</Label><Input value={wm.text} placeholder="e.g. DRAFT" onChange={(e) => set({ watermark: { ...wm, text: e.target.value } })} data-testid="setup-watermark-text" /></div>
                <div className="grid grid-cols-2 gap-4 items-end">
                  <label className="flex items-center gap-2 text-sm">Colour<input type="color" value={wm.color} onChange={(e) => set({ watermark: { ...wm, color: e.target.value } })} data-testid="setup-watermark-color" /></label>
                  <div className="space-y-2"><Label className="text-xs">Transparency · {Math.round(wm.opacity * 100)}%</Label>
                    <Slider min={5} max={60} value={[Math.round(wm.opacity * 100)]} onValueChange={([v]) => set({ watermark: { ...wm, opacity: v / 100 } })} data-testid="setup-watermark-opacity" /></div>
                </div>
              </TabsContent>
              <TabsContent value="numbers" className="mt-0 space-y-4">
                <label className="flex items-center gap-3 text-sm"><Switch checked={pn.enabled} onCheckedChange={(v) => set({ pageNumbers: { ...pn, enabled: v } })} data-testid="setup-page-numbers-switch" />Show page numbers</label>
                <div className="grid grid-cols-3 gap-2">
                  {["top-left", "top-center", "top-right", "bottom-left", "bottom-center", "bottom-right"].map((p) => (
                    <Choice key={p} on={pn.position === p} onClick={() => set({ pageNumbers: { ...pn, enabled: true, position: p } })} testid={`setup-pn-${p}`}><span className="capitalize text-xs">{p.replace("-", " ")}</span></Choice>
                  ))}
                </div>
                <div className="space-y-1"><Label className="text-xs">Format — use {"{n}"} for the page and {"{total}"} for the page count</Label>
                  <Input value={pn.format} onChange={(e) => set({ pageNumbers: { ...pn, format: e.target.value } })} data-testid="setup-pn-format" /></div>
              </TabsContent>
            </div>
            <div><PaperThumb s={settings} /><p className="text-[11px] text-center text-muted-foreground">{PAGE_SIZES[settings.size].label.split("·")[0]} · {settings.orientation}</p></div>
          </div>
        </Tabs>
        <DialogFooter><Button onClick={onClose} data-testid="page-setup-done-btn">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
